import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { modalStyles as styles } from './modalStyles';
import { colors } from '../theme';
import { formatDDMMYYYY } from '../utils/dates';

const API_BASE = process.env.REACT_APP_API_URL;

const SCORE_LABELS = {
  0: 'Very poor', 1: 'Poor', 2: 'Below average', 3: 'Below average',
  4: 'Average', 5: 'Average', 6: 'Good', 7: 'Good',
  8: 'Very good', 9: 'Excellent', 10: 'Outstanding'
};

export default function CustomerRmaModalShipped({ rma, onClose }) {
  const { getToken, isSignedIn } = useAuth();
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [receiptConfirmed, setReceiptConfirmed] = useState(false);
  const [closingCase, setClosingCase] = useState(false);
  const [closeError, setCloseError] = useState('');
  // Track local status so UI updates immediately after customer closes
  const [localStatus, setLocalStatus] = useState(rma?.status);
  const [localArchivedId, setLocalArchivedId] = useState(
    rma?.status === 'archived' ? rma?._id : null
  );

  const dispatch = rma?.shipping?.dispatch && typeof rma.shipping.dispatch === 'object'
    ? rma.shipping.dispatch : {};
  const trackingNumber = dispatch.trackingNumber || rma?.trackingNumber || '';
  const carrier = dispatch.carrier || '';
  const shippedAt = dispatch.shippedAt ? formatDDMMYYYY(dispatch.shippedAt) : null;
  const deliveredAt = rma?.shipping?.deliveryConfirmedAt
    ? formatDDMMYYYY(rma.shipping.deliveryConfirmedAt) : null;

  const isArchived = localStatus === 'archived';
  const hasSatisfaction = rma?.satisfaction?.score != null;
  const showSurvey = isArchived && !hasSatisfaction && !feedbackDone;

  const handleConfirmReceipt = async () => {
    if (!receiptConfirmed) return;
    setCloseError('');
    setClosingCase(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (isSignedIn) {
        const token = await getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(`${API_BASE}/rma/archive/${rma._id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ outcome: {} }),
      });
      const data = await res.json();
      setLocalArchivedId(data.archived?._id || rma._id);
      setLocalStatus('archived');
    } catch (err) {
      console.error('Failed to confirm receipt', err);
      setCloseError('Something went wrong. Please try again.');
    } finally {
      setClosingCase(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (score === null) return;
    setSubmitting(true);
    const targetId = localArchivedId || rma._id;
    try {
      await fetch(`${API_BASE}/rma/archived/${targetId}/satisfaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, comment })
      });
      setFeedbackDone(true);
    } catch (err) {
      console.error('Failed to save satisfaction', err);
      setFeedbackDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: '480px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            backgroundColor: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.1rem', flexShrink: 0
          }}>
            {isArchived ? '✓' : '🚚'}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', color: colors.textStrong }}>
              {isArchived ? 'Case Closed' : 'Device Shipped'}
            </h2>
            <div style={{ fontSize: '0.82rem', color: colors.textMuted, marginTop: '0.1rem' }}>
              {rma?.rmaId || rma?._id}
            </div>
          </div>
        </div>

        {/* Status message */}
        <div style={{
          borderRadius: '12px',
          border: `1px solid ${isArchived ? 'rgba(70,187,137,0.3)' : 'rgba(99,179,237,0.3)'}`,
          backgroundColor: isArchived ? 'rgba(70,187,137,0.07)' : 'rgba(99,179,237,0.07)',
          padding: '1rem 1.1rem', marginBottom: '1.25rem'
        }}>
          <p style={{ margin: 0, fontSize: '0.88rem', color: colors.textBody, lineHeight: 1.6 }}>
            {isArchived
              ? 'Your device has been repaired and returned. This case is now closed.'
              : 'Your device has been dispatched and is on its way back to you. Once you receive it, please confirm below to close the case.'}
          </p>
        </div>

        {/* Case details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
            <span style={{ color: colors.textMuted }}>Serial number</span>
            <span style={{ color: colors.textStrong, fontWeight: 600 }}>{rma?.serialNumber}</span>
          </div>
          {shippedAt && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span style={{ color: colors.textMuted }}>Dispatched</span>
              <span style={{ color: colors.textStrong, fontWeight: 600 }}>{shippedAt}</span>
            </div>
          )}
          {deliveredAt && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span style={{ color: colors.textMuted }}>Delivered</span>
              <span style={{ color: colors.primary, fontWeight: 600 }}>{deliveredAt}</span>
            </div>
          )}
          {carrier && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span style={{ color: colors.textMuted }}>Carrier</span>
              <span style={{ color: colors.textStrong, fontWeight: 600 }}>{carrier}</span>
            </div>
          )}
          {trackingNumber && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span style={{ color: colors.textMuted }}>Tracking number</span>
              <span style={{ color: colors.textStrong, fontWeight: 600, wordBreak: 'break-all' }}>
                {trackingNumber}
              </span>
            </div>
          )}
        </div>

        {/* Receipt confirmation — shown when shipped */}
        {localStatus === 'shipped' && (
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '1.25rem',
            marginBottom: '1.25rem'
          }}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 600, color: colors.textStrong }}>
              Confirm receipt
            </p>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.65rem', cursor: 'pointer',
              padding: '0.75rem', borderRadius: 8, marginBottom: '0.9rem',
              border: `1px solid ${receiptConfirmed ? 'rgba(70,187,137,0.35)' : colors.panelBorder}`,
              background: receiptConfirmed ? 'rgba(70,187,137,0.06)' : 'rgba(255,255,255,0.02)'
            }}>
              <input
                type="checkbox"
                checked={receiptConfirmed}
                onChange={e => setReceiptConfirmed(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: colors.primary, flexShrink: 0, marginTop: 2 }}
              />
              <span style={{ fontSize: '0.88rem', color: colors.textBody, lineHeight: 1.5 }}>
                I confirm I have received my device —{' '}
                <strong style={{ color: colors.textStrong }}>{rma?.serialNumber}</strong>
              </span>
            </label>
            {closeError && (
              <p style={{ color: colors.danger, fontSize: '0.85rem', marginBottom: '0.65rem' }}>{closeError}</p>
            )}
            <button
              onClick={handleConfirmReceipt}
              disabled={!receiptConfirmed || closingCase}
              style={{
                ...styles.button,
                opacity: !receiptConfirmed || closingCase ? 0.45 : 1,
                cursor: !receiptConfirmed || closingCase ? 'not-allowed' : 'pointer'
              }}
            >
              {closingCase ? 'Closing…' : 'Close Case'}
            </button>
          </div>
        )}

        {/* Satisfaction survey */}
        {showSurvey && (
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '1.25rem',
            marginBottom: '1.25rem'
          }}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 600, color: colors.textStrong }}>
              How would you rate your service experience?
            </p>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              {[...Array(11)].map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setScore(i)}
                  style={{
                    width: '2.1rem', height: '2.1rem', borderRadius: 6, fontSize: '0.82rem',
                    fontWeight: score === i ? 700 : 400, cursor: 'pointer',
                    border: `1px solid ${score === i ? colors.primary : colors.panelBorder}`,
                    background: score === i ? 'rgba(70,187,137,0.18)' : 'rgba(255,255,255,0.04)',
                    color: score === i ? colors.primary : colors.textMuted,
                  }}
                >
                  {i}
                </button>
              ))}
            </div>
            {score !== null && (
              <p style={{ margin: '0.25rem 0 0.75rem', fontSize: '0.8rem', color: colors.primary }}>
                {SCORE_LABELS[score]}
              </p>
            )}
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Optional comment..."
              rows={2}
              style={{
                width: '100%', padding: '0.5rem 0.65rem', borderRadius: 6, resize: 'vertical',
                border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.05)',
                color: colors.textStrong, fontSize: '0.85rem', boxSizing: 'border-box',
                marginBottom: '0.75rem'
              }}
            />
            <button
              onClick={handleSubmitFeedback}
              disabled={score === null || submitting}
              style={{
                ...styles.button,
                opacity: score === null ? 0.4 : 1,
                cursor: score === null ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Submitting…' : 'Submit feedback'}
            </button>
          </div>
        )}

        {feedbackDone && (
          <p style={{ fontSize: '0.85rem', color: colors.primary, marginBottom: '1rem' }}>
            Thank you for your feedback!
          </p>
        )}

        {hasSatisfaction && (
          <p style={{ fontSize: '0.82rem', color: colors.textMuted, marginBottom: '1rem' }}>
            Feedback already submitted. Thank you.
          </p>
        )}

        <button onClick={onClose} style={{ ...styles.button, background: 'rgba(255,255,255,0.06)', color: colors.textBody }}>
          Close
        </button>
      </div>
    </div>
  );
}
